// helpers
import React from "react";
import config from "../constants/config";
import { grids } from "../selectors/index";
import * as ls from "local-storage";
import {
  AchievementType,
  EquationType,
  GameType,
  OperatorType,
} from "../types/generalTypes";
import { GridDataType } from "./storage";
import { Capacitor } from "@capacitor/core";
import Fraction from "fraction.js";
import { Box } from "@mui/material";
export const getPlatform = () => {
  const cap = Capacitor.getPlatform(); // "ios" | "android" | "web"
  return cap === "web" ? "browser" : cap;
};
// console.log("ls grids", ls.get<string[]>("grids"));
export const isValidEquation = (eq: EquationType) => {
  const numbers = eq.filter(Number) as number[];

  // No duplicate numbers
  if (
    numbers.length !== numbers.filter((n, i) => numbers.indexOf(n) === i).length
  )
    return false;

  // Check all even positions are numbers
  if (!eq.filter((a, i) => i % 2 === 0).every(Number)) return false;

  // Check all odd positions are operators
  if (
    (eq.filter((a, i) => i % 2 === 1) as OperatorType[]).some(
      (o) => config.operators.indexOf(o) < 0,
    )
  )
    return false;

  return true;
};
export const isValidEquationWithGrid = (eq: EquationType, grid: number[]) => {
  const numbers = eq.filter(Number) as number[];

  // check all numbers are tiles in the grid
  if (numbers.some((n) => grid.indexOf(n) < 0)) return false;

  if (!isValidEquation(eq)) return false;

  return true;
};

// const getEq = state => state.eq.slice(0, state.eqPos);

export const getTotal = (a: EquationType) => {
  if (!isValidEquation(a)) return NaN;
  let rt: number = (a[0] as number) || 0;

  for (let i = 1; i + 1 < a.length; i += 2) {
    if (a[i] === "×") rt *= a[i + 1] as number;
    if (a[i] === "÷") rt /= a[i + 1] as number;
    if (a[i] === "+") rt += a[i + 1] as number;
    if (a[i] === "-") rt -= a[i + 1] as number;
  }
  return rt;
};

export const formatEq = (eq: EquationType) => {
  const lines = [];

  if (eq.length === 0) return [];

  lines.push(<li key={0}>{eq[0]}</li>); // first number by itself

  for (let i = 1; i + 1 < eq.length; i += 2) {
    lines.push(
      <li key={i}>
        {eq.slice(i, i + 2).join(" ")} = {getTotal(eq.slice(0, i + 2))}
      </li>,
    );
  }
  return (
    <div>
      <ul className="fullcalc">{lines}</ul>
      <div className="onelinecalc">
        {eq.join(" ")} = <strong>{getTotal(eq)}</strong>
      </div>
    </div>
  );
};
/*
export const getAchievementCount = solutions =>
  config.achievements.filter(a => solutions.some(a.check)).length;
*/
// Standardise an equation for comparing so that 4 x 5 + 6 - 7 is the same as 5 x 4 - 7 + 6
export const standardEq = (e: EquationType) => {
  if (e.length <= 1) return e;
  let standard: Array<[OperatorType, string]> = [];
  let currentOp: string = "+";
  let group: string[] = [];
  const eq = e.slice();
  eq.unshift("×");
  eq.unshift(1); // was "1"
  eq.unshift("+");
  eq.push("×"); // was "!"
  eq.push(0);

  for (let i = 0; i + 1 < eq.length; i += 2) {
    if (
      !(
        ["+", "-"].indexOf(eq[i] as string) >= 0 &&
        ["+", "-"].indexOf(currentOp) >= 0
      ) &&
      !(
        ["×", "÷"].indexOf(eq[i] as string) >= 0 &&
        ["×", "÷"].indexOf(currentOp) >= 0
      )
    ) {
      // standard = standard.concat([].concat.apply([],
      // group.slice().sort().map(a => [ a.substr(-1), a.substr(0,a.length-1) ])));
      standard = [
        ...standard,
        ...group
          .slice()
          .sort()
          .map(
            (a) =>
              [a.substring(a.length - 1), a.substring(0, a.length - 1)] as [
                OperatorType,
                string,
              ],
          ),
      ];
      group = [];
    }
    group.push("0".repeat(5 - eq[i + 1].toString().length) + eq[i + 1] + eq[i]);
    currentOp = eq[i] as string;
  }

  return standard;
};

export const uniqueSolutions = (a: EquationType[]) => {
  const standard = a.map((n) => standardEq(n).join(" "));

  return a.filter(
    (elem, pos) => standard.indexOf(standardEq(elem).join(" ")) === pos,
  );
};

export const gridSizeTiles = (s: string) => {
  const [w, h] = s.split("x").map((a) => parseInt(a, 10));
  return w * h;
};

export const hasAchieved = (
  achievement: AchievementType,
  solutions: EquationType[],
  target: number,
  grid: number[],
) => solutions.some((eq) => achievement.check(eq, target, grid, solutions));

export const getStoredData = (grid: string) => {
  // const d = JSON.parse(localStorage.getItem(grid));
  // console.log("getStoredData localStorage", d);
  // console.log("getStoredData ls", ls(grid));
  const d = ls.get<GridDataType>(grid);
  if (d && d.solutions && d.solutions.length > 0) return d;
  return { solutions: [] }; // default data
};
export const setStoredData = (grid: string, data: GridDataType) => {
  // console.log("setting data", grid, data);
  ls.set(grid, data);
  ls.set("currentGrid", grid);
  if ((ls.get<string[]>("grids") || []).indexOf(grid) < 0) {
    // console.log("NEW GRID");
    ls.set("grids", [
      ...(ls.get<string[]>("grids") || []).filter((g) => g !== grid),
      grid,
    ]);
    grids.push(grid);
  }
  // console.log('testing set data', localStorage.getItem(grid));
};
// export const getOldGridList = () =>
//   Object.keys(localStorage)
//     .filter((text) =>
//       /^[\],:{}\s]*$/.test(
//         text
//           .replace(/\\["\\/bfnrtu]/g, "@")
//           .replace(
//             /"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?/g,
//             "]"
//           )
//           .replace(/(?:^|:|,)(?:\s*\[)+/g, "")
//       )
//     )
//     .filter((a) => ls.get<GridDataType>(a).solutions);

export const checkGridData = (addLog: (l: string) => void) => {
  // console.log("checkGD", ls.get<string[]>("grids"));
  const g = ls.get<string[]>("grids");
  addLog("grids: " + JSON.stringify(g));
  if (!g || g.length === 0) {
    addLog("no grids in localStorage");
  }
};
export const getGridData = () => {
  return ls.get<string[]>("grids") || [];
  //  || getOldGridList()
};

export const findFactors = (n: number) => {
  const numFactors = [];

  for (let i = 1; i <= Math.floor(Math.sqrt(n)); i += 1) {
    if (n % i === 0) {
      numFactors.push(i);
      if (n / i !== i) numFactors.push(n / i);
    }
  }
  numFactors.sort((x, y) => x - y);
  return numFactors;
};
const findFactorSolution = (tiles: number[], target: number) => {
  if (!Number.isInteger(target)) return [];
  // console.log('find factors', tiles, target);
  const f = findFactors(target).filter(
    (a) => a !== 1 && a !== target && tiles.indexOf(a) >= 0,
  );
  // console.log('matching factors', target, f, f.map(a => (target / a)));
  return f;
};
const findOtherSolution = (tiles: number[], target: number) => {
  // find any 2 tiles with any op
  const sols: EquationType[] = [];
  tiles.forEach((t1) => {
    tiles
      .filter((t) => t !== t1)
      .forEach((t2) => {
        config.operators.forEach((o) => {
          if (getTotal([t1, o, t2]) === target) sols.push([t1, o, t2]);
          else {
            tiles
              .filter((t) => t !== t1 && t !== t2)
              .forEach((t3) => {
                config.operators.forEach((o2: OperatorType) => {
                  if (getTotal([t1, o, t2, o2, t3]) === target)
                    sols.push([t1, o, t2, o2, t3]);
                });
              });
          }
        });
      });
  });
  // console.log('fOS', tiles, target, sols);
  return sols;
};
// '+', '-', '×', '÷']

const opOp = (op: OperatorType) => {
  if (op === "×") return "÷";
  if (op === "÷") return "×";
  if (op === "+") return "-";
  return "+";
};

const findAllFactorSolution = (tiles: number[], target: number) => {
  const solutions: EquationType[] = [];
  findFactorSolution(tiles, target).forEach((s) => {
    findOtherSolution(
      tiles.filter((t) => t !== s),
      target / s,
    ).forEach((ss) => {
      // (A +-*/ B) * C
      solutions.push([...ss, "×", s]);
    });
  });
  findOtherSolution(tiles, target).forEach((sol) => {
    // (A +-*/ B)
    solutions.push(sol);
  });

  // getTotal
  /*
  tiles.forEach((t, i) => {
    config.operators.forEach((o) => {
      findOtherSolution(
        [...tiles.slice(0, i), ...tiles.slice(i + 1)],
        getTotal([target, o, t]),
      ).forEach((sol) => {
        console.log('BLAH!!!!', sol);
        // looking for (a + b) = total - c
        // Same as above?
        // solutions.push(sol);
      });
      findFactorSolution(
        [...tiles.slice(0, i), ...tiles.slice(i + 1)],
        getTotal([target, o, t]),
      ).forEach((s) => {
        console.log('sol2', [(getTotal([target, o, t]) / s), '×', s, opOp(o), t].join(' '));
      });
    });
  });
  */
  return solutions;
};

export const findSolutions = (tiles: number[], target: number) => {
  const bestmultSort = (a: EquationType, b: EquationType) =>
    a.length - b.length;

  let solutions = findAllFactorSolution(tiles, target);

  config.operators.forEach((o: OperatorType) => {
    tiles.forEach((t) => {
      solutions = [
        ...solutions,
        ...findAllFactorSolution(
          tiles.filter((tile) => tile !== t),
          getTotal([target, o, t]),
        ).map((s) => [...s, opOp(o), t] as EquationType),
      ];
    });
  });

  solutions = solutions.sort(bestmultSort);
  // Remove duplicates
  solutions = uniqueSolutions(solutions.slice(0, 20));

  /*
  console.log(`Target: ${target}, Tiles: ${tiles}`);
  console.log('Factors', findFactors(target));
  for (let i = 0; i < Math.min(15, solutions.length); i += 1) {
    console.log(`${solutions[i].join(' ')} = ${getTotal(solutions[i])}`);
  }
  */
  return solutions;
};

const displayFraction = (
  numerator: number,
  denominator: number,
): JSX.Element => {
  if (denominator === 1) return <Box component="span">{numerator}</Box>;
  return (
    <Box component="span" position="relative" pl={2}>
      <Box
        component="span"
        sx={{
          fontSize: "0.5em",
          position: "absolute",
          top: 0,
          textAlign: "right",
          right: "40%",
        }}
      >
        {numerator}
      </Box>
      <Box
        component="span"
        pl={1}
        fontSize="0.8em"
        position="relative"
        top={-6}
        left={-1}
      >
        /
      </Box>

      <Box
        component="span"
        sx={{
          fontSize: "0.5em",
          position: "absolute",
          bottom: 4,
        }}
      >
        {denominator}
      </Box>
    </Box>
  );
};

export const formatTotal = (t: number): JSX.Element => {
  const fraction = [
    Math.floor(t),
    new Fraction(t - Math.floor(t)).toFraction(),
  ] as [number, string];

  if (fraction[1] === "0") return <Box component="span">{fraction[0]}</Box>;
  return (
    <Box component="span">
      {fraction[0] !== 0 && <Box component="span">{fraction[0]}</Box>}
      <Box component="span">
        {displayFraction(
          ...(fraction[1].split("/").map((a) => parseInt(a)) as [
            number,
            number,
          ]),
        )}
      </Box>
    </Box>
  );
};
export const formatTotalDecimal = (t: number) => {
  let out = t.toFixed(3).replace(/0/g, " ").trim();
  if (out.startsWith(".")) out = "0" + out;
  return out.replace(/ /g, "0").replace(/\./g, " ").trim().replace(/ /g, ".");
};
export const shuffle = (a: number[]) => {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};
// export const VALID_HASH_CHARS =
//   "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-._~!$&'()*+,;=:@/?";
// export const VALID_HASH_CHARS =
//   "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ-._~!$&'()*+,;=:@/?";
export const VALID_HASH_CHARS =
  "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ_~$&'*+=:@/?";
export const getGridDiff = (grid: number[]) => {
  const sorted = [...grid].sort((a, b) => a - b);
  return sorted.map((a, ai) => (ai > 0 ? a - sorted[ai - 1] : a));
};
export const getGridBiggestDiff = (grid: number[]) =>
  Math.max(...getGridDiff(grid));

const SWAP_CHARS = [
  "cbc",
  "bdb",
  "dbb",
  "bcc",
  "ccb",
  "bbd",
  "bcb",
  "cbb",
  "bbc",
  "bbb",
];
export const convertGameToHash = (game: GameType) => {
  // VALID_HASH_CHARS
  const t =
    VALID_HASH_CHARS[Math.floor(game.target / VALID_HASH_CHARS.length)] +
    VALID_HASH_CHARS[game.target % VALID_HASH_CHARS.length];
  const grid = getGridDiff(game.grid).map((a) => VALID_HASH_CHARS[a]);

  const checksum =
    VALID_HASH_CHARS[
      (game.grid.reduce((a, b) => a + b, 0) + game.target) %
        VALID_HASH_CHARS.length
    ];

  let out = grid.join("") + t + checksum;

  SWAP_CHARS.forEach((c, i) => {
    out = out.replace(new RegExp(c, "g"), i.toString());
  });

  return out;
};
export const convertHashToGame = (h: string) => {
  let hash = h;

  SWAP_CHARS.forEach((c, i) => {
    hash = hash.replace(new RegExp(i.toString(), "g"), c);
  });
  const checksum = hash.substr(-1);
  const targetHash = hash
    .substr(-3, 2)
    .split("")
    .map((a) => VALID_HASH_CHARS.indexOf(a));
  const gridDiffs = hash
    .substr(0, hash.length - 3)
    .split("")
    .map((a) => VALID_HASH_CHARS.indexOf(a));
  const grid: number[] = [];
  gridDiffs.forEach((a, ai) => {
    grid[ai] = a + (ai > 0 ? grid[ai - 1] : 0);
  });
  const target = targetHash[0] * VALID_HASH_CHARS.length + targetHash[1];

  // gridDiffs.map((a, ai) => a + (ai > 0 ? gridDiffs[ai - 1] : 0));
  const game = { target, grid: shuffle(grid) };

  const checksumCorrect =
    VALID_HASH_CHARS[
      (game.grid.reduce((a, b) => a + b, 0) + game.target) %
        VALID_HASH_CHARS.length
    ];

  // console.log("convertHashToGame", game, checksum, checksumCorrect);
  if (checksumCorrect !== checksum) return false;
  return game;
};
